// Layer 3 (Transformation): "Month 4 to close" (locked comp `planning.html` A,
// the 4-column panel beside the chart). It reads rollingForecastView()'s
// forecast-to-close; the only new arithmetic is bar geometry.

import { type AnnualPlanView, type MonthlyPlanView, type PlanningScenarioName, type RollingForecastView } from './Scene.transform';

export type MonthCloseScenario = {
  name: PlanningScenarioName;
  rateStph: number;
  basis: string;
  /** The provenance the route has always carried for this rate (tooltip). */
  provenance: string;
  closeSt: number;
  /** The row whose close the panel already prints as its primary figure: it
   *  restates neither that figure nor the running rate. */
  repeatsPrimary: boolean;
};

export type MonthCloseRead = {
  title: string;
  centralCloseSt: number;
  centralPctOfBudget: number;
  monthBudgetSt: number;
  stphNeededToClose: number;
  runningStph: number;
  actualMtdSt: number;
  windowLabel: string;
  remainingHours: number;
  /** Bar geometry against the month's ceiling (the bar's full width, D27). */
  actualPct: number;
  centralRemainderPct: number;
  budgetPct: number;
  scenarios: MonthCloseScenario[];
  formula: string;
};

/** Rate basis per scenario, as the forecast names it. D27 (K16): the Optimistic
 *  rate is the Capacity engine's maximum achievable feed, named as `/capacity`
 *  names it (`grinding ceiling` at the cursor), read off the rolling view. */
export function scenarioRateBasis(rolling: RollingForecastView): Record<PlanningScenarioName, { basis: string; provenance: string }> {
  return {
    Optimistic: { basis: rolling.optimisticBasis, provenance: rolling.optimisticProvenance },
    Central: { basis: '7-day feed rate', provenance: '[derived] 7-day feed rate, measured rs-ore-td complete production days' },
    Conservative: { basis: '7-day rate − 1 σ', provenance: '[derived] 7-day feed rate − 1 σ, measured rs-ore-td complete production days' },
  };
}

export const SCENARIO_ORDER: readonly PlanningScenarioName[] = ['Optimistic', 'Central', 'Conservative'];

export function monthCloseRead(
  monthly: MonthlyPlanView,
  annual: AnnualPlanView,
  rolling: RollingForecastView,
): MonthCloseRead {
  const close = rolling.forecastToClose;
  const monthRow = monthly.rows[3]!;
  const central = close.scenarios.find((scenario) => scenario.name === 'Central')!;
  const ceiling = annual.hierarchy.find((row) => row.id === 'month')?.ceilingSt ?? Math.max(...close.scenarios.map((s) => s.tonnes));
  const pct = (value: number) => Math.max(0, Math.min(100, (value / ceiling) * 100));
  return {
    title: `Month ${monthRow.id.slice(1)} to close`,
    centralCloseSt: central.tonnes,
    centralPctOfBudget: close.centralPctOfPlan,
    monthBudgetSt: monthRow.budgetSt,
    stphNeededToClose: close.stphNeededToClose,
    runningStph: rolling.sevenDayStph,
    actualMtdSt: close.actualMtdSt,
    windowLabel: monthly.mtdWindowLabel,
    remainingHours: close.remainingHours,
    actualPct: pct(close.actualMtdSt),
    centralRemainderPct: pct(central.tonnes) - pct(close.actualMtdSt),
    budgetPct: pct(monthRow.budgetSt),
    scenarios: SCENARIO_ORDER.map((name) => {
      const scenario = close.scenarios.find((row) => row.name === name)!;
      return {
        name,
        rateStph: scenario.rateStph,
        ...scenarioRateBasis(rolling)[name],
        closeSt: scenario.tonnes,
        repeatsPrimary: name === 'Central',
      };
    }),
    formula: `M4 to date + ${Math.round(close.remainingHours).toLocaleString('en-US')} h × rate, to ${close.closeAt}`,
  };
}
